import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  Plus, 
  Clock, 
  MapPin, 
  User, 
  Mail, 
  Phone, 
  FileText,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Building2,
  Edit,
  Eye,
  ChevronRight as ChevronRightIcon,
  Info,
  Target,
  TrendingUp,
  Smartphone,
  Monitor
} from 'lucide-react';

export const CalendarHelpPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      id: 'header',
      title: 'Header e Navigazione',
      description: 'La sezione superiore della pagina offre accesso rapido alle funzioni principali.',
      details: [
        'Pulsante "Indietro" per tornare alla dashboard',
        'Titolo della pagina con descrizione',
        'Pulsante "Aggiorna" per ricaricare i colloqui',
        'Pulsante "Nuovo Colloquio" per aggiungere un appuntamento'
      ],
      icon: <Calendar className="h-5 w-5" />
    },
    {
      id: 'stats',
      title: 'Statistiche Rapide',
      description: 'Quattro card che mostrano un riepilogo immediato dei tuoi colloqui.',
      details: [
        '**Totale**: Numero complessivo di colloqui programmati',
        '**Mese**: Colloqui del mese corrente',
        '**Oggi**: Colloqui programmati per oggi',
        '**Prossimi**: Colloqui futuri ancora da fare'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      id: 'calendar-controls',
      title: 'Controlli Calendario',
      description: 'Strumenti per navigare tra i mesi e visualizzare i colloqui.',
      details: [
        '**Navigazione mesi**: Frecce per andare avanti/indietro nel tempo',
        '**Mese corrente**: Visualizzazione chiara del mese e anno selezionato',
        '**Giorni evidenziati**: I giorni con colloqui sono colorati di blu',
        '**Giorno oggi**: Evidenziato con un bordo blu e un punto'
      ],
      icon: <Target className="h-5 w-5" />
    },
    {
      id: 'calendar-grid',
      title: 'Griglia Calendario',
      description: 'Visualizzazione mensile dei colloqui con indicatori visivi.',
      details: [
        '**Giorni della settimana**: Inizio settimana lunedì',
        '**Giorni con colloqui**: Evidenziati in blu con contatore',
        '**Giorno selezionato**: Anello blu intorno al giorno cliccato',
        '**Giorno oggi**: Bordo blu e punto indicatore'
      ],
      icon: <Calendar className="h-5 w-5" />
    },
    {
      id: 'interview-details',
      title: 'Dettagli Colloquio',
      description: 'Informazioni complete su ogni colloquio programmato.',
      details: [
        '**Posizione lavorativa**: Ruolo per cui hai il colloquio',
        '**Nome azienda**: Azienda che ti ha contattato',
        '**Tipo colloquio**: Presenza, Online o Telefono',
        '**Orario**: Data e ora dell\'appuntamento',
        '**Luogo/Link**: Indirizzo o link per il colloquio'
      ],
      icon: <Building2 className="h-5 w-5" />
    },
    {
      id: 'contact-info',
      title: 'Informazioni di Contatto',
      description: 'Dettagli completi sulla persona di riferimento.',
      details: [
        '**Persona di riferimento**: Nome del contatto aziendale',
        '**Ruolo**: Posizione lavorativa del contatto',
        '**Email**: Indirizzo email per comunicazioni',
        '**Telefono**: Numero di telefono per contatti urgenti'
      ],
      icon: <User className="h-5 w-5" />
    },
    {
      id: 'interview-types',
      title: 'Tipi di Colloquio',
      description: 'Diversi formati di colloquio supportati.',
      details: [
        '**🏢 Presenza**: Colloquio fisico presso l\'azienda',
        '**💻 Online**: Videochiamata o meeting virtuale',
        '**📱 Telefono**: Chiamata telefonica tradizionale'
      ],
      icon: <Smartphone className="h-5 w-5" />
    },
    {
      id: 'mobile-features',
      title: 'Funzionalità Mobile',
      description: 'Caratteristiche specifiche per dispositivi mobili.',
      details: [
        '**Layout ottimizzato**: Statistiche in una riga, controlli compatti',
        '**Comportamento dropdown**: Calendario si nasconde quando si seleziona un giorno',
        '**Touch-friendly**: Pulsanti grandi e facili da premere',
        '**Responsive**: Si adatta automaticamente alla schermata'
      ],
      icon: <Smartphone className="h-5 w-5" />
    },
    {
      id: 'desktop-features',
      title: 'Funzionalità Desktop',
      description: 'Caratteristiche specifiche per schermi grandi.',
      details: [
        '**Layout a due colonne**: Calendario a sinistra, dettagli a destra',
        '**Card dettagli**: Visualizzazione completa dei colloqui del giorno',
        '**Navigazione avanzata**: Controlli più grandi e dettagliati',
        '**Hover effects**: Interazioni avanzate con mouse'
      ],
      icon: <Monitor className="h-5 w-5" />
    }
  ];

  const tips = [
    'Clicca su un giorno per vedere i dettagli dei colloqui',
    'I giorni con colloqui sono evidenziati in blu nel calendario',
    'Su mobile, il calendario si nasconde quando selezioni un giorno',
    'Usa le frecce per navigare tra i mesi',
    'Il pulsante "Aggiorna" ricarica tutti i colloqui',
    'Puoi modificare un colloquio cliccando sui dettagli',
    'Le note ti aiutano a ricordare dettagli importanti',
    'I promemoria ti avvisano prima del colloquio'
  ];

  const workflow = [
    {
      step: 1,
      title: 'Programma un Colloquio',
      description: 'Clicca su "Nuovo Colloquio" e compila tutti i dettagli'
    },
    {
      step: 2,
      title: 'Seleziona la Candidatura',
      description: 'Scegli la candidatura associata al colloquio'
    },
    {
      step: 3,
      title: 'Inserisci i Dettagli',
      description: 'Compila data, ora, tipo e luogo del colloquio'
    },
    {
      step: 4,
      title: 'Aggiungi Contatti',
      description: 'Inserisci le informazioni della persona di riferimento'
    },
    {
      step: 5,
      title: 'Monitora il Calendario',
      description: 'Controlla regolarmente i colloqui programmati'
    }
  ];

  const interviewTypes = [
    {
      type: 'Presenza',
      icon: '🏢',
      description: 'Colloquio fisico presso l\'azienda',
      color: 'bg-blue-100 text-blue-800'
    },
    {
      type: 'Online',
      icon: '💻',
      description: 'Videochiamata o meeting virtuale',
      color: 'bg-green-100 text-green-800'
    },
    {
      type: 'Telefono',
      icon: '📱',
      description: 'Chiamata telefonica tradizionale',
      color: 'bg-purple-100 text-purple-800'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/help')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl">
                <Calendar className="h-6 w-6 text-purple-700" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Guida Calendario Colloqui
                </h1>
                <p className="text-sm text-gray-600">
                  Scopri come gestire efficacemente tutti i tuoi colloqui di lavoro
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Panoramica */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Info className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Panoramica
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Il Calendario Colloqui è il tuo assistente personale per gestire tutti gli appuntamenti di lavoro. 
                Qui puoi visualizzare, programmare e gestire tutti i tuoi colloqui in un unico posto. 
                Il calendario è progettato per essere intuitivo e efficiente, permettendoti di tenere traccia 
                di tutti i tuoi appuntamenti professionali.
              </p>
            </div>
          </div>
        </div>

        {/* Funzionalità */}
        <div className="space-y-4 lg:space-y-6">
          {features.map((feature, index) => (
            <div key={feature.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRightIcon className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: detail }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tipi di Colloquio */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Tipi di Colloquio Supportati
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {interviewTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{type.icon}</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${type.color}`}>
                    {type.type}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5" />
            Workflow Consigliato
          </h3>
          <div className="space-y-3">
            {workflow.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {step.step}
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900 text-sm">{step.title}</h4>
                  <p className="text-blue-800 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suggerimenti */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
            <Info className="h-5 w-5" />
            Suggerimenti Utili
          </h3>
          <ul className="space-y-2">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-green-800">
                <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Azioni */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pronto a gestire i tuoi colloqui?
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/calendar')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                <Calendar className="h-4 w-4" />
                Vai al Calendario
              </button>
              <button
                onClick={() => navigate('/calendar')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Nuovo Colloquio
              </button>
              <button
                onClick={() => navigate('/help')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <FileText className="h-4 w-4" />
                Altre Guide
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 